import { useEffect, useState, useCallback } from "react";
import { Navigate } from "react-router-dom";
import { QRCodeSVG } from "qrcode.react";
import { ApiService } from "../../services/api";
import { isYesToolsHostname } from "../../utils/yesTools";

interface QrStatus {
  hasQr: boolean;
  poolReset?: boolean;
  code: string | null;
  assignedAt: string | null;
  expiresAt: string | null;
  isExpired: boolean;
  entryTime: string | null;
  exitTime: string | null;
}

const formatTime = (iso: string | null) => {
  if (!iso) return null;
  return new Date(iso).toLocaleTimeString("tr-TR", { hour: "2-digit", minute: "2-digit" });
};

export default function AkademiQrPage() {
  const isYesToolsDomain =
    typeof window !== "undefined" && isYesToolsHostname(window.location.hostname);

  const [status, setStatus] = useState<QrStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [requesting, setRequesting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchStatus = useCallback(async () => {
    try {
      const res = await ApiService.getApi("akademi/qr/status");
      setStatus(res?.data || null);
      setError(null);
    } catch (e: any) {
      setError(e?.message || "QR durumu alınamadı.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStatus();
    const interval = setInterval(fetchStatus, 10_000);
    return () => clearInterval(interval);
  }, [fetchStatus]);

  const handleRequest = async () => {
    setRequesting(true);
    setError(null);
    try {
      await ApiService.postApi("akademi/qr/request", {});
      await fetchStatus();
    } catch (e: any) {
      setError(e?.message || "QR oluşturulamadı.");
    } finally {
      setRequesting(false);
    }
  };

  if (!isYesToolsDomain) {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <div className="flex flex-col gap-5 p-4 md:p-6 max-w-md mx-auto w-full">
      <div>
        <h1 className="text-xl font-bold text-white">Akademi Günlük QR</h1>
        <p className="text-sm text-white/60 mt-1">
          Kapıda bu QR'ı okutarak giriş ve çıkış yapabilirsiniz.
        </p>
      </div>

      {loading ? (
        <div className="bg-white border border-gray-200 rounded-xl p-10 flex items-center justify-center">
          <span className="text-sm text-gray-400">Yükleniyor...</span>
        </div>
      ) : error ? (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-sm text-red-700">
          {error}
        </div>
      ) : status?.hasQr ? (
        <div className="flex flex-col gap-4">
          {status.isExpired ? (
            <div className="bg-white border border-gray-200 rounded-xl p-8 flex flex-col items-center gap-3 text-center">
              <div className="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center text-xl">🔒</div>
              <div>
                <p className="text-sm font-semibold text-gray-800">Bugünkü QR'ınız Sona Erdi</p>
                <p className="text-xs text-gray-400 mt-1">Yarın yeni bir QR alabilirsiniz.</p>
              </div>
            </div>
          ) : (
            <div className="bg-white border border-gray-200 rounded-xl p-6 flex flex-col items-center gap-4">
              <div className="p-3 bg-white rounded-lg shadow-sm border border-gray-100">
                <QRCodeSVG value={status.code!} size={200} level="M" includeMargin={false} />
              </div>
              <p className="text-xs text-gray-400">Gece 00:00'a kadar geçerlidir.</p>
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <TimeCard
              label="Giriş"
              time={formatTime(status.entryTime)}
              color="green"
              emptyText="Henüz Giriş Yapılmadı"
            />
            <TimeCard
              label="Çıkış"
              time={formatTime(status.exitTime)}
              color="blue"
              emptyText="Henüz Çıkış Yapılmadı"
            />
          </div>

          {!status.isExpired && status.exitTime && (
            <button
              onClick={handleRequest}
              disabled={requesting}
              className="w-full py-3 px-4 rounded-xl border border-gray-200 bg-white text-sm font-semibold text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {requesting ? "Oluşturuluyor..." : "Yeni QR Oluştur"}
            </button>
          )}

        </div>
      ) : (
        <div className="bg-white border border-gray-200 rounded-xl p-8 flex flex-col items-center gap-4 text-center">
          <div className="w-14 h-14 rounded-full bg-gray-100 flex items-center justify-center text-2xl">📱</div>
          <div>
            <p className="text-sm font-semibold text-gray-800">
              {status?.poolReset ? "QR Havuzu Güncellendi" : "Bugün İçin QR Kodunuz Yok"}
            </p>
            <p className="text-xs text-gray-400 mt-1">
              {status?.poolReset
                ? "Yeni bir QR kodu oluşturabilirsiniz."
                : "Aşağıdaki butona basarak günlük QR kodunuzu oluşturun."}
            </p>
          </div>
          <button
            onClick={handleRequest}
            disabled={requesting}
            className="px-6 py-2.5 rounded-xl bg-blue-700 text-white text-sm font-semibold hover:bg-blue-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {requesting ? "Oluşturuluyor..." : "QR Oluştur"}
          </button>
        </div>
      )}
    </div>
  );
}

function TimeCard({
  label,
  time,
  color,
  emptyText,
}: {
  label: string;
  time: string | null;
  color: "green" | "blue";
  emptyText: string;
}) {
  const active = time !== null;
  const colorMap = {
    green: {
      border: active ? "border-green-200" : "border-gray-100",
      bg: active ? "bg-green-50" : "bg-gray-50",
      label: active ? "text-green-700" : "text-gray-400",
      value: "text-green-700",
    },
    blue: {
      border: active ? "border-blue-200" : "border-gray-100",
      bg: active ? "bg-blue-50" : "bg-gray-50",
      label: active ? "text-blue-700" : "text-gray-400",
      value: "text-blue-700",
    },
  };
  const c = colorMap[color];

  return (
    <div className={`rounded-xl border ${c.border} ${c.bg} p-4`}>
      <p className={`text-xs font-bold uppercase tracking-wide ${c.label} mb-2`}>{label}</p>
      {active ? (
        <p className={`text-2xl font-extrabold ${c.value} leading-none`}>{time}</p>
      ) : (
        <p className="text-xs text-gray-400 leading-snug">{emptyText}</p>
      )}
    </div>
  );
}
